// Kostenlos-Strecke im Browser (KS1, docs/strategie/kostenlos-starten.md Abschnitte 4 und 8).
//
// Klicks: Die Links einer Strecken-Mail führen direkt auf die Seite und tragen `mk_mail` (Zeichen der Mail) und
// `mk_knopf` (crm-strecke-versand/logik.ts, linksMarkieren()). Beim ersten Laden meldet `mailKlickErfassen()` den Klick an
// crm-strecke-link und nimmt beide aus der Adresse, bevor der Router sie liest. Ohne Weiterleitung zählen Mailfilter,
// die Links vorab öffnen, nicht mit; schlägt das Melden fehl, merkt niemand etwas.

import { supabase } from "@/integrations/supabase/client";

/**
 * Kopie des Schalters aus supabase/functions/_shared/kostenlos-strecke.ts (dort wirkt er). Seit Daniels „Start“
 * `true`; Test: src/lib/marketing/__tests__/strecke.test.ts.
 */
export const KOSTENLOS_STRECKE_FUER_ALLE = true;

const ZEICHEN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KNOPF = /^[a-z0-9_:-]{1,60}$/;

export interface MailKlick {
  /** Zeichen der Mail, leer bei unbrauchbaren Angaben (die trotzdem aus der Adresse verschwinden). */
  zeichen: string;
  knopf: string;
  /** Die übrigen Angaben der Adresse, mit „?“ oder leer. */
  rest: string;
}

/** Liest mk_mail und mk_knopf aus der Adresse; null, wenn keins von beiden da ist. */
export function mailKlickLesen(suche: string): MailKlick | null {
  const p = new URLSearchParams(suche);
  if (!p.has("mk_mail") && !p.has("mk_knopf")) return null;
  const zeichen = (p.get("mk_mail") ?? "").trim();
  const knopf = (p.get("mk_knopf") ?? "").trim().toLowerCase();
  p.delete("mk_mail");
  p.delete("mk_knopf");
  const rest = p.toString();
  const gueltig = ZEICHEN.test(zeichen) && KNOPF.test(knopf);
  return { zeichen: gueltig ? zeichen : "", knopf: gueltig ? knopf : "", rest: rest ? `?${rest}` : "" };
}

let erfasst = false;

/** Einmal je Seitenaufruf, vor dem ersten Rendern (AttributionCapture). */
export function mailKlickErfassen(): void {
  if (erfasst || typeof window === "undefined") return;
  erfasst = true;
  try {
    const klick = mailKlickLesen(window.location.search);
    if (!klick) return;
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${klick.rest}${window.location.hash}`);
    if (!klick.zeichen) return;
    void supabase.functions
      .invoke("crm-strecke-link", {
        body: { art: "klick", m: klick.zeichen, k: klick.knopf, ziel: window.location.pathname },
      })
      .catch(() => undefined);
  } catch {
    /* Messen darf nie stören */
  }
}

// Themen-Messung (KS3, Plan Abschnitt 8): angesehene Kursseiten und Lektionen. Gefragt wird nur bei Konten mit Stufe
// `kostenlos` (der Aufrufer prüft das, ab dem Start also nur kostenlose Konten); ob gemessen wird, entscheidet
// crm_strecke_aufruf() in der Datenbank (nur Konten in einer Strecke oder kurz davor, nie Ausbildungen). Je Seite und
// Ziel einmal, ein Fehler stört nie.

const gemerkt = new Set<string>();

export function aufrufMerken(art: "kurs" | "lektion", id: string | null | undefined): void {
  if (!id || !ZEICHEN.test(id)) return;
  const schluessel = `${art}:${id}`;
  if (gemerkt.has(schluessel)) return;
  gemerkt.add(schluessel);
  try {
    void Promise.resolve(supabase.rpc("crm_strecke_aufruf", { p_art: art, p_id: id })).catch(() => undefined);
  } catch {
    /* Messen darf nie stören */
  }
}

// Punktestand (KS4, Plan Abschnitt 8): „Preisseite angesehen“. Wie die Themen-Messung nur bei Stufe `kostenlos` gefragt;
// ob gezählt wird, entscheidet crm_strecke_signal() (nur Konten in einer Strecke oder kurz davor, eine Zeile je Tag).
// Je Seite einmal, ein Fehler stört nie.

/** Signale, die der Browser melden darf; gleich der Liste in crm_strecke_signal() (Test streckeZahlen.test.ts). */
export const BROWSER_SIGNALE = ["preisseite"] as const;
export type BrowserSignal = (typeof BROWSER_SIGNALE)[number];

const gemeldet = new Set<BrowserSignal>();

export function signalMerken(signal: BrowserSignal): void {
  if (gemeldet.has(signal)) return;
  gemeldet.add(signal);
  try {
    void Promise.resolve(supabase.rpc("crm_strecke_signal", { p_signal: signal })).catch(() => undefined);
  } catch {
    /* Messen darf nie stören */
  }
}
